import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import MasterLayout from '../../../masterLayout/MasterLayout';
import Breadcrumb from '../../../components/Breadcrumb';
import {
  AppButton,
  AppCard,
  AppCheckbox,
  AppEmptyState,
  AppFormField,
  AppInput,
  AppSelect,
  AppSpinner,
} from '../../../components/ui';
import secteursAdminService from '../../../services/secteursAdminService';
import secteursService from '../../../services/secteursService';
import { notify } from '../../../components/common/AppToast';
import { ROUTES } from '../../../config/routes';

const SecteursAdminZoneEditPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    nomZone: '', codeCommune: '', codeDepartement: '', codeRegion: '', codePostal: '', active: 1, secteur_id: '',
  });
  const [secteurs, setSecteurs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState(null);

  useEffect(() => {
    Promise.all([
      secteursAdminService.getZone(id),
      secteursService.getSecteurs(),
    ])
      .then(([zone, secteursList]) => {
        setSecteurs(secteursList);
        setForm({
          nomZone: zone.nomZone || '',
          codeCommune: zone.codeCommune || '',
          codeDepartement: zone.codeDepartement || '',
          codeRegion: zone.codeRegion || '',
          codePostal: zone.codePostal || '',
          active: zone.active ? 1 : 0,
          secteur_id: zone.secteur?.id ? String(zone.secteur.id) : '',
        });
      })
      .catch((err) => {
        setLoadError(err.message);
        notify.error(err.message);
      })
      .finally(() => setLoading(false));
  }, [id]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await secteursAdminService.updateZone(id, {
        ...form,
        active: form.active ? 1 : 0,
        secteur_id: form.secteur_id ? Number(form.secteur_id) : null,
      });
      notify.success('Zone enregistrée');
      navigate(ROUTES.secteurs.adminZones);
    } catch (err) {
      notify.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <MasterLayout>
        <AppSpinner />
      </MasterLayout>
    );
  }

  return (
    <MasterLayout>
      <Breadcrumb
        title="Modifier la zone"
        actions={(
          <div className="d-flex gap-2">
            <AppButton as={Link} to={ROUTES.secteurs.adminZones} size="sm" variant="outline-secondary">Retour</AppButton>
            <AppButton as={Link} to={ROUTES.secteurs.adminZonePolygon(id)} size="sm" variant="outline-success">
              Éditer le polygone
            </AppButton>
          </div>
        )}
      />

      {loadError ? (
        <AppEmptyState message="Zone introuvable" />
      ) : (
        <AppCard>
          <form onSubmit={handleSubmit}>
            <div className="row g-3">
              <AppFormField className="col-md-6" label="Nom de la zone" htmlFor="zone-nom" required>
                <AppInput
                  id="zone-nom"
                  type="text"
                  required
                  value={form.nomZone}
                  onChange={(e) => setForm((f) => ({ ...f, nomZone: e.target.value }))}
                />
              </AppFormField>
              <AppFormField className="col-md-6" label="Code commune" htmlFor="zone-commune" required>
                <AppInput
                  id="zone-commune"
                  type="text"
                  required
                  value={form.codeCommune}
                  onChange={(e) => setForm((f) => ({ ...f, codeCommune: e.target.value }))}
                />
              </AppFormField>
              <AppFormField className="col-md-4" label="Code département" htmlFor="zone-dept">
                <AppInput
                  id="zone-dept"
                  type="text"
                  value={form.codeDepartement}
                  onChange={(e) => setForm((f) => ({ ...f, codeDepartement: e.target.value }))}
                />
              </AppFormField>
              <AppFormField className="col-md-4" label="Code région" htmlFor="zone-region">
                <AppInput
                  id="zone-region"
                  type="text"
                  value={form.codeRegion}
                  onChange={(e) => setForm((f) => ({ ...f, codeRegion: e.target.value }))}
                />
              </AppFormField>
              <AppFormField className="col-md-4" label="Code postal" htmlFor="zone-cp">
                <AppInput
                  id="zone-cp"
                  type="text"
                  value={form.codePostal}
                  onChange={(e) => setForm((f) => ({ ...f, codePostal: e.target.value }))}
                />
              </AppFormField>
              <AppFormField className="col-md-6" label="Secteur commercial" htmlFor="zone-secteur">
                <AppSelect
                  id="zone-secteur"
                  value={form.secteur_id}
                  onChange={(e) => setForm((f) => ({ ...f, secteur_id: e.target.value }))}
                >
                  <option value="">Aucun secteur</option>
                  {secteurs.map((s) => (
                    <option key={s.id} value={String(s.id)}>
                      {s.nomCommercial}{s.nomSecteur ? ` — ${s.nomSecteur}` : ''}
                    </option>
                  ))}
                </AppSelect>
              </AppFormField>
              <div className="col-md-6 d-flex align-items-end">
                <AppCheckbox
                  id="active"
                  label="Zone active"
                  checked={Boolean(form.active)}
                  onChange={(e) => setForm((f) => ({ ...f, active: e.target.checked ? 1 : 0 }))}
                />
              </div>
            </div>
            <div className="d-flex justify-content-end gap-2 mt-4">
              <AppButton as={Link} to={ROUTES.secteurs.adminZones} variant="secondary">Annuler</AppButton>
              <AppButton type="submit" variant="success" loading={saving} loadingLabel="Enregistrement…">
                Enregistrer
              </AppButton>
            </div>
          </form>
        </AppCard>
      )}
    </MasterLayout>
  );
};

export default SecteursAdminZoneEditPage;
